import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ApiError } from '../Models/ApiModel';
import type { EnsembleDraftModel, EnsembleModel } from '../Models/EnsembleModel';
import { EnsembleService } from '../Services/EnsembleService';

/**
 * The groups a tutor teaches.
 *
 * Shaped exactly like the roster slice next door, for the same reason: the
 * screens that draw a group need its members and its weekly hours to hand,
 * and refetching for every card is not an option.
 */

export type LoadState = 'idle' | 'loading' | 'ready' | 'failed';

export interface EnsemblesState {
  items: EnsembleModel[];
  state: LoadState;
  error: string | null;
}

const initialState: EnsemblesState = { items: [], state: 'idle', error: null };

function message(error: unknown): string {
  return error instanceof ApiError ? error.code : 'SERVER_ERROR';
}

export const loadEnsembles = createAsyncThunk('ensembles/load', async () => EnsembleService.list());

export const saveEnsemble = createAsyncThunk(
  'ensembles/save',
  async (input: { name?: string; draft: EnsembleDraftModel }) => {
    if (!input.name) return EnsembleService.create(input.draft);
    return EnsembleService.update(input.name, input.draft);
  }
);

export const removeEnsemble = createAsyncThunk('ensembles/remove', async (name: string) => {
  await EnsembleService.remove(name);
  return name;
});

export const setEnsembleMembers = createAsyncThunk(
  'ensembles/setMembers',
  async (input: { name: string; members: string[] }) => ({
    name: input.name,
    members: await EnsembleService.setMembers(input.name, input.members)
  })
);

function sortByName(items: EnsembleModel[]): void {
  items.sort((a, b) => a.name.localeCompare(b.name));
}

const ensemblesSlice = createSlice({
  name: 'ensembles',
  initialState,
  reducers: {
    /** Applied after a change elsewhere, so no full refetch is needed. */
    upsert(state, action: PayloadAction<EnsembleModel>) {
      const index = state.items.findIndex(ensemble => ensemble.name === action.payload.name);
      if (index === -1) state.items.push(action.payload);
      else state.items[index] = action.payload;
      sortByName(state.items);
    }
  },
  extraReducers: builder => {
    builder
      .addCase(loadEnsembles.pending, state => {
        state.state = 'loading';
        state.error = null;
      })
      .addCase(loadEnsembles.fulfilled, (state, action) => {
        state.items = action.payload;
        state.state = 'ready';
      })
      .addCase(loadEnsembles.rejected, (state, action) => {
        state.state = 'failed';
        state.error = message(action.error);
      })
      .addCase(saveEnsemble.fulfilled, (state, action) => {
        const saved = action.payload;
        // A rename comes back with the name it used to have. Dropping the old
        // entry is what keeps the list from showing the group twice.
        const previous = (saved as { previousName?: string }).previousName;
        if (previous) state.items = state.items.filter(ensemble => ensemble.name !== previous);

        const index = state.items.findIndex(ensemble => ensemble.name === saved.name);
        const existing = index === -1 ? undefined : state.items[index];
        // Every relation is listed explicitly. One left out of this merge does
        // not keep its old value, it becomes undefined, and the next component
        // that reads it takes the whole tree down with it.
        const merged: EnsembleModel = {
          ...saved,
          members: saved.members ?? existing?.members ?? [],
          slots: saved.slots ?? existing?.slots ?? []
        };
        if (index === -1) state.items.push(merged);
        else state.items[index] = merged;
        sortByName(state.items);
      })
      .addCase(setEnsembleMembers.fulfilled, (state, action) => {
        const entry = state.items.find(ensemble => ensemble.name === action.payload.name);
        if (entry) entry.members = action.payload.members;
      })
      .addCase(removeEnsemble.fulfilled, (state, action) => {
        state.items = state.items.filter(ensemble => ensemble.name !== action.payload);
      });
  }
});

export const { upsert } = ensemblesSlice.actions;
export default ensemblesSlice.reducer;
